import { convertToRaw, Modifier } from 'draft-js';
import draftToHtml from 'draftjs-to-html';

/**
 * Converts mentions in the editorState to the format {{mentionName&mentionId}} and returns the HTML string representation of the EditorState.
 * @param {EditorState} editorState
 * @returns
 */
export const toHtmlWithMentions = editorState => {
  const rawContentState = convertToRaw(editorState.getCurrentContent());

  // Transforming 'mention' entities preserves mention info; otherwise, they appear as plain text.
  const customEntityTransform = (entity, text) => {
    if (entity?.type === 'mention') {
      const textWithoutAtSign = text.slice(1);
      return `<span>{{${textWithoutAtSign}&${entity?.data?.mention?.id}}}</span>`;
    }
    return text;
  };

  const html = draftToHtml(rawContentState, undefined, undefined, customEntityTransform);

  return html;
};

/**
 *
 * Converts mentions in the contentState from the format {{mentionName&mentionId}}
 * back into Draft.js entities, allowing for proper handling of mentions in the editor.
 * @param {ContentState} contentState
 * @returns
 */
export const toDraftWithMentions = contentState => {
  const blockMap = contentState.getBlockMap();

  let newContentState = contentState;

  blockMap.forEach(block => {
    let blockText = block.getText();
    let entityRanges = [];
    let numberOfRemovedCharacters = 0;

    blockText = blockText.replace(/{{(.*?)}}/g, (__match, mentionNameAndId, offset) => {
      const [mentionName, mentionId] = mentionNameAndId.split('&');

      // Create a new entity for mention
      newContentState = newContentState.createEntity('mention', 'IMMUTABLE', {
        mention: {
          id: mentionId,
          name: mentionName,
        },
      });

      const entityKey = newContentState.getLastCreatedEntityKey();

      entityRanges.push({ offset: offset - numberOfRemovedCharacters, length: mentionName.length + 1, entityKey });
      // 4 curly brackets + mentionId.length + & - @
      numberOfRemovedCharacters += 4 + mentionId.length;
      return `@${mentionName}`;
    });

    // Create a new block with the updated text
    const newBlock = block.merge({
      text: blockText,
      characterList: block.getCharacterList().map((char, index) => {
        const entityKey = entityRanges.find(range => index >= range.offset && index < range.offset + range.length)
          ?.entityKey;
        return char.merge({ entity: entityKey || null });
      }),
    });

    // Update the newContentState with the new block
    newContentState = newContentState.merge({
      blockMap: newContentState.getBlockMap().set(block.getKey(), newBlock),
    });

    // Apply entities to text
    entityRanges.forEach(({ offset, length, entityKey }) => {
      const selection = newContentState.getSelectionAfter().merge({
        anchorOffset: offset,
        focusOffset: offset + length,
      });

      newContentState = Modifier.applyEntity(newContentState, selection, entityKey);
    });
  });

  return newContentState;
};
